/**
 * Channel-neutral model for dynamic content slots: an element in a message
 * whose content varies by audience branch. Branches keep the shape returned by
 * the dynamic-slots loaders; the wire parsing and DOM/HTML helpers stay with
 * each channel's editor.
 */

import { isFallbackBranch } from "@hightouch/lib/llm/lifecycle-studio/dynamic-content/types";
import type { AssignmentSource } from "@hightouch/lib/llm/lifecycle-studio/dynamic-content/types";
import { ConditionType } from "src/types/visual";
import type { RootCondition } from "src/types/visual";
import { uniqueName } from "src/utils/unique-name";

export type SlotAssignmentType = "conditional";

// `audience_schema` holds audience ASTs on `condition`, `esp_profile` free-text
// predicates on `espCondition`. Fallback detection is shared with the backend.
export { isFallbackBranch };
export type { AssignmentSource };

export type SlotBranch = {
  id: string;
  name: string;
  /** Audience condition AST (query builder format); null = the required
   * "Everyone else" fallback, always ordered last. Also null on esp_profile
   * branches, whose logic lives on `espCondition`. */
  condition: RootCondition | null;
  /** ESP-native bare predicate (destination dialect, e.g. Braze Liquid
   * `custom_attribute.${tier} == 'gold'`) when the library's
   * assignment_source is `esp_profile`. Null/absent on the fallback and on
   * audience-mode branches. */
  espCondition?: string | null;
  /** The slot element's outerHTML (marker stripped) when this branch is
   * assigned — rendering replaces the marked element wholesale. Must be a
   * single root element; empty = this audience sees nothing. */
  content: string;
  /** Usage-scoped content row id (`content_dynamic_slot_contents.id`). The
   * loader hydrates it on every saved branch; only client-minted branches
   * not yet saved lack it. Writes reference the existing row by this id
   * instead of re-sending inline HTML — see `slot-write-branches.ts`. */
  contentId?: string;
};

/** The branch's authored ESP predicate, or null when blank/absent. */
export function branchEspCondition(branch: {
  espCondition?: string | null;
}): string | null {
  const trimmed = branch.espCondition?.trim();
  return trimmed ? trimmed : null;
}

/** True for a condition that exists but has no rules yet: the empty group
 * `createConditionalBranch` seeds, and what `rebuildBranches` writes to keep an
 * unassigned variant alive. The deploy compiler allows these — the variant just
 * ships to no one, with a warning — so the canvas warns on the card too. Null
 * (the fallback) is not "empty" — it deliberately has no condition. */
export function isEmptyCondition(condition: RootCondition | null): boolean {
  return (
    condition !== null &&
    "conditions" in condition &&
    Array.isArray(condition.conditions) &&
    condition.conditions.length === 0
  );
}

/** Compact human-readable rendering of a branch condition for chips,
 * tooltips, and the deploy recap. Full editing happens in the query builder. */
export function summarizeCondition(condition: RootCondition): string {
  const conditions =
    "conditions" in condition && Array.isArray(condition.conditions)
      ? condition.conditions
      : [condition];
  if (conditions.length === 0) return "No conditions yet";
  const joiner = condition.type === ConditionType.Or ? " or " : " and ";
  const parts = conditions.map((c) => {
    if (c?.type === ConditionType.Property && typeof c.property === "string") {
      const op = c.operator === "!=" ? "is not" : (c.operator ?? "is");
      const opLabel = op === "=" ? "is" : op;
      return `${c.property} ${opLabel} ${c.value ?? ""}`.trim();
    }
    return "1 rule";
  });
  return parts.join(joiner);
}

/** True when the branch carries logic of its own — an authored condition in
 * either mode. False for the fallback (which matches by having none) and for a
 * variant's unassigned carrier branch. */
export function branchHasLogic(branch: SlotBranch): boolean {
  return (
    (branch.condition !== null && !isEmptyCondition(branch.condition)) ||
    branchEspCondition(branch) !== null
  );
}

/** Compact one-line rendering of whichever kind of logic the branch holds:
 * the raw ESP predicate, the summarized audience condition, or null for the
 * fallback. */
export function summarizeBranchLogic(branch: {
  condition: RootCondition | null;
  espCondition?: string | null;
}): string | null {
  const espCondition = branchEspCondition(branch);
  if (espCondition !== null) return espCondition;
  if (branch.condition !== null) return summarizeCondition(branch.condition);
  return null;
}

export type DynamicSlot = {
  id: string;
  /** Library / slot display name shown in the canvas. */
  name: string;
  assignmentType: SlotAssignmentType;
  /** Which kind of condition the slot's library holds — stamped by the
   * backend at create time (workspace/campaign/brand default chain), so the
   * conditions dialog edits in the right mode even before any condition is
   * authored. */
  assignmentSource: AssignmentSource;
  /** Waterfall order: first matching condition wins; fallback last. */
  branches: SlotBranch[];
};

/**
 * A distinct piece of content in a slot — what the canvas shows as a card and
 * the conditions dialog assigns.
 *
 * A branch is a CONDITION, not a variant: several branches may share one
 * content row (N conditions : 1 variant), so anything the user thinks of as
 * "a variant" has to be derived rather than read off a branch.
 */
export type SlotVariant = {
  /** The shared content row id, or the branch id for a branch with no saved
   * content row yet (kept inline on save). */
  id: string;
  /** The first stored branch's name — a stand-in until the loader exposes the
   * content row's own name. Branches sharing a row can hold divergent names;
   * renaming a variant writes all of them (`renameVariant`). */
  name: string;
  contentId?: string;
  content: string;
  /** Stored branches showing this content, in stored order. Writes fan out
   * over these; a save reclaims the ids so branch identity is stable across
   * dialog round-trips. */
  branchIds: string[];
};

/** What the assign dropdowns need from a variant. */
export type VariantOption = Pick<SlotVariant, "id" | "name">;

/**
 * The variant a branch belongs to — its content row, or the branch's own id
 * when it has no row yet (client-minted and unsaved, or a saved branch the
 * loader's LEFT JOIN found no content for). Keying those on `undefined`
 * instead would collapse every one of them into a single variant.
 */
export function branchVariantId(branch: SlotBranch): string {
  return branch.contentId ?? branch.id;
}

/**
 * A variant identity that survives a save, for client-side state that has to
 * outlive a slots reload (today: canvas comment scopes). `SlotVariant.id`
 * flips the first time the slot is written — persistence mints a content row
 * for an inline branch. The FIRST stored branch id, not the smallest: the
 * conditions dialog reclaims stored ids in stored order (`claimBranch`), so a
 * variant that gains or loses a condition keeps it, while the smallest can
 * move (branch ids are random uuids).
 */
export function stableVariantId(variant: SlotVariant): string {
  return variant.branchIds[0] ?? variant.id;
}

/** The slot's distinct variants, in stored branch order. */
export function slotVariants(branches: SlotBranch[]): SlotVariant[] {
  const byId = new Map<string, SlotVariant>();
  for (const branch of branches) {
    const id = branchVariantId(branch);
    const existing = byId.get(id);
    if (existing) {
      existing.branchIds.push(branch.id);
    } else {
      byId.set(id, {
        id,
        name: branch.name,
        ...(branch.contentId === undefined
          ? {}
          : { contentId: branch.contentId }),
        content: branch.content,
        branchIds: [branch.id],
      });
    }
  }
  return [...byId.values()];
}

/** Default display name for a newly marked slot: the element's type label,
 * numbered only when the bare name is taken ("Image", "Image 2", …). */
export function nextDefaultSlotName(
  existingNames: string[],
  elementType: string,
): string {
  return uniqueName(elementType, existingNames);
}

export function createFallbackBranch(content: string): SlotBranch {
  return {
    id: crypto.randomUUID(),
    name: "Everyone else",
    condition: null,
    content,
  };
}

export function createConditionalBranch(
  content: string,
  name = "New branch",
): SlotBranch {
  return {
    id: crypto.randomUUID(),
    name,
    condition: {
      id: crypto.randomUUID(),
      type: ConditionType.And,
      conditions: [],
    },
    content,
  };
}

/** A new canvas-added variant: unassigned, numbered after the existing
 * variants, seeded from the "Everyone else" variant's content when one is
 * assigned (else the first branch's). Numbered off distinct variants, not
 * branches — two conditions sharing a variant must not skip a number. */
export function seedNewVariant(slot: DynamicSlot): SlotBranch {
  const seed = slot.branches.find(isFallbackBranch) ?? slot.branches[0];
  return createConditionalBranch(
    seed?.content ?? "",
    `Variant ${slotVariants(slot.branches).length + 1}`,
  );
}

/** Waterfall invariant: conditional branches in order, fallback pinned last. */
export function sortBranches(branches: SlotBranch[]): SlotBranch[] {
  const conditional = branches.filter((b) => !isFallbackBranch(b));
  const fallback = branches.filter(isFallbackBranch);
  return [...conditional, ...fallback];
}
